import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import {
  getStoredUser,
  getStoredRoles,
  getStoredPermissions,
  getRememberMe,
} from '@/services/keycloakAuthService';

/**
 * Hydrates the Zustand auth store from SecureStorage on app load.
 * Handles the case where the user refreshes the page and Zustand
 * persist may not have the latest data from SecureStorage.
 */
export const useHydrateAuth = () => {
  useEffect(() => {
    const hydrate = async () => {
      const storedUser = await getStoredUser();
      if (!storedUser) return;

      const currentUser = useAuthStore.getState().user;

      // Only hydrate if Zustand store is empty or has a different user
      if (!currentUser || currentUser.id !== storedUser.id) {
        const roles = (await getStoredRoles()) ?? [];
        const permissions = (await getStoredPermissions()) ?? [];

        useAuthStore.getState().login({
          id: storedUser.id,
          email: storedUser.email,
          first_name: storedUser.first_name ?? '',
          last_name: storedUser.last_name ?? '',
          full_name: `${storedUser.first_name ?? ''} ${storedUser.last_name ?? ''}`.trim(),
          roles,
          permissions,
          phone: storedUser.phone,
          avatar_url: (storedUser.avatar_url as string) ?? null,
          organization_name: storedUser.organization_name as string | undefined,
        });
      }

      // Re-set session marker on page refresh (sessionStorage survives refresh
      // but is cleared on browser close, which is exactly what we need)
      const rememberMe = await getRememberMe();
      if (!rememberMe) {
        sessionStorage.setItem('session_alive', '1');
      }
    };

    void hydrate();
  }, []);
};
